import React, { useEffect, useState } from 'react';
import { Box, Button, SxProps, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight } from '@fortawesome/pro-duotone-svg-icons';
import { useSociete, useTiersService, useUtilisateurService } from '@europrocurement/l2d-domain';
import { type UtilisateursJsonldUtilisateurRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useModal } from '@europrocurement/flexy-components/providers';
import { isWithinWorkingHours } from '@europrocurement/l2d-utils';
import { type GenericModalMailType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import {
    type ContactsContactJsonldContactRead,
    type UtilisateurPrescripteurReadCommerciaux,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { CustomAvatar } from '../../molecules';
import { InformationChip, LinkCallablePhone } from '../../atoms';

export type userTypeType = 'CONTACT' | 'UTILISATEUR' | 'COMMERCIAL';
export type SourceType = 'FRONT' | 'BACK';

export type UserCardUserType = {
    mail?: string | null;
    phone?: string | null;
    mobilePhone?: string | null;
    firstName?: string;
    lastName?: string;
    userIdentifier?: string;
    serviceLibelle?: string | null;
    poste?: string | null;
};
export type UserContactCardProps = {
    source: SourceType;
    userId: number;
    userProps?: UtilisateursJsonldUtilisateurRead;
    displayUserSchedule: boolean;
    userType: userTypeType;
    displayActionContact: boolean;
    onActionClick?: (props?: unknown) => void;
    defaultMailAction?: (props?: GenericModalMailType) => void;
    mailSubject?: string;
    tiersIds?: number[]; // Used on search contacts
};

const UserContactCard: React.FunctionComponent<UserContactCardProps> = function ({
    source,
    userId,
    userProps,
    userType,
    displayUserSchedule,
    displayActionContact = false,
    onActionClick,
    defaultMailAction,
    mailSubject,
    tiersIds,
}) {
    // Find User from id Props ?
    const { palette } = useTheme();
    const { utilisateurApi } = useUtilisateurService();
    const { contactsApi } = useTiersService();
    const keycloakService = UseKeycloakService();
    const { modalActions } = useModal();
    const xIdSociete = useSociete();

    const [user, setUser] = useState<
        | UtilisateursJsonldUtilisateurRead
        | UtilisateurPrescripteurReadCommerciaux
        | ContactsContactJsonldContactRead
        | undefined
    >(userProps);
    const [displayedUser, setDisplayedUser] = useState<UserCardUserType>();
    const { apiErrorMessageSnackbar } = useHandleError();

    // Fetch user by Id id if not passed thru props
    useEffect(() => {
        if (!userProps && userId) {
            if (userType === 'CONTACT') {
                contactsApi
                    .getContactContactsItem({ id: userId.toString(), xIdSociete })
                    .then((res) => setUser(res.data))
                    .catch((err) =>
                        apiErrorMessageSnackbar(
                            err,
                            "Une erreur est survenue lors de la récupération de l'utilisateur",
                        ),
                    );
            } else {
                utilisateurApi
                    .apiUtilisateursIdGet({ id: userId.toString() })
                    .then((res) => setUser(res.data))
                    .catch((err) =>
                        apiErrorMessageSnackbar(
                            err,
                            "Une erreur est survenue lors de la récupération de l'utilisateur",
                        ),
                    );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Hydrate data from user based on user Type and source (front or back)
    useEffect(() => {
        if (user) {
            setDisplayedUser({});
            let mobilePhone;
            let mail;
            let serviceLibelle;
            let poste;
            let phone;
            let firstName;
            let lastName;

            if (userType === 'COMMERCIAL') {
                const commercial = user as UtilisateurPrescripteurReadCommerciaux;
                mail = commercial.email;
                poste = commercial.poste;
                phone = commercial.ligneDirecte;
                firstName = commercial.prenom;
                lastName = commercial.nom;
                if (source === 'BACK') {
                    mobilePhone = commercial.portable;
                }
            } else if (userType === 'CONTACT') {
                const contact = user as ContactsContactJsonldContactRead;
                const contactMail = contact.emailsContact.find(
                    (contactMails) => contactMails.defaut && contactMails.email,
                );
                if (contactMail) {
                    mail = contactMail.email;
                }
                const contactPhone = contact.telephonesContact.find(
                    (contactPhones) => contactPhones.defaut && contactPhones.telephone,
                );
                if (contactPhone) {
                    phone = contactPhone.telephone;
                }
                firstName = contact.prenomContact;
                lastName = contact.nomContact;
            } else {
                const utilisateur = user as UtilisateursJsonldUtilisateurRead;
                mail = utilisateur.mail;
                phone = utilisateur.ligneDirecte;
                if (utilisateur.groupeTravail && utilisateur.groupeTravail.length > 0) {
                    const activGroup = utilisateur.groupeTravail.find(
                        (group) => group.fDelete === false,
                    );
                    if (activGroup?.email) {
                        mail = activGroup?.email;
                    }
                    if (activGroup?.phone) {
                        phone = activGroup?.phone;
                    }
                }
                serviceLibelle = utilisateur.service?.libelle;
                poste = utilisateur.poste;
                firstName = utilisateur.prenom;
                lastName = utilisateur.nom;
                if (source === 'BACK') {
                    mobilePhone = utilisateur.portable;
                }
            }

            setDisplayedUser({
                mail,
                phone,
                firstName,
                lastName,
                userIdentifier: user.userIdentifier,
                mobilePhone,
                serviceLibelle,
                poste,
            });
        }
    }, [user, userType, source]);

    const handleButtonClick = () => {
        if (displayedUser && displayedUser.mail) {
            if (typeof onActionClick !== 'undefined') {
                onActionClick();
            } else if (typeof defaultMailAction !== 'undefined') {
                let mailBodyContent = `Bonjour ${displayedUser.firstName}  ${displayedUser.lastName}\n\n\n\n`;
                mailBodyContent += `Cordialement,\n${keycloakService.getName()}\n${keycloakService.getEmail()}`;

                defaultMailAction({
                    to: [
                        {
                            firstName: displayedUser.firstName as string,
                            lastName: displayedUser.lastName as string,
                            emailAddress: displayedUser.mail,
                        },
                    ],
                    subject: mailSubject,
                    body: mailBodyContent,
                    showBody: true,
                    showSubject: true,
                    showCC: true,
                    searchParamsTiersIds: tiersIds,
                });
            }
        }
    };

    const flexCenter: SxProps = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

    const isWithinWorkingHour =
        isWithinWorkingHours(9 * 60, 17 * 60) && !isWithinWorkingHours(12 * 60, 14 * 60);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                width: '332px',
                position: 'relative',
            }}
        >
            {/* Title and return action */}
            <Box
                sx={{ ...flexCenter, justifyContent: 'start', gap: '16px' }}
                onClick={() => modalActions.close()}
            >
                {displayedUser?.serviceLibelle && (
                    <Typography variant="l2dh6">Service {displayedUser.serviceLibelle}</Typography>
                )}
            </Box>

            {/* Header avatar / name Status */}
            {displayedUser && (
                <Box
                    sx={{
                        ...flexCenter,
                        borderBottom: `1px solid ${palette.grey[100]}`,
                        padding: '8px 0 16px 0',
                    }}
                >
                    <CustomAvatar
                        sx={{
                            width: '90px',
                            height: '90px',
                        }}
                        firstName={displayedUser.firstName}
                        lastName={displayedUser.lastName}
                        keycloakId={
                            displayedUser.userIdentifier ? displayedUser.userIdentifier : undefined
                        }
                    />
                    <Box sx={{ flex: '1', paddingLeft: '24px' }}>
                        <Box
                            sx={{
                                ...flexCenter,
                                flexDirection: 'column',
                                alignItems: 'start',
                                gap: '4px',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ fontWeight: '600' }}
                            >
                                {displayedUser.firstName} {displayedUser.lastName}
                            </Typography>

                            {displayedUser.serviceLibelle && (
                                <Typography
                                    variant="l2dcaption"
                                    sx={{ color: 'text.gris70' }}
                                >
                                    {displayedUser.serviceLibelle}
                                </Typography>
                            )}

                            <InformationChip
                                color={
                                    isWithinWorkingHour ? palette.success.main : palette.grey[700]
                                }
                                label={
                                    <Box
                                        sx={{
                                            ...flexCenter,
                                            justifyContent: 'space-between',
                                            gap: '2px',
                                            padding: '6px 12px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '15px',
                                                height: '15px',
                                                borderRadius: '100%',
                                                backgroundColor: isWithinWorkingHour
                                                    ? palette.success.main
                                                    : palette.grey[700],
                                            }}
                                        />
                                        <Typography>
                                            {isWithinWorkingHour ? 'Disponible' : 'Hors ligne'}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Content rows */}
            <Box sx={{ ...flexCenter, flexDirection: 'column', alignItems: 'start', gap: '12px' }}>
                {displayedUser?.mail && (
                    <Box
                        sx={{ ...flexCenter, alignItems: 'start', gap: '16px', cursor: 'pointer' }}
                        onClick={handleButtonClick}
                    >
                        <Box sx={flexCenter}>
                            <FontAwesomeIcon
                                color={palette.grey[500]}
                                icon={faEnvelope}
                            />
                        </Box>
                        <Typography sx={{ color: palette.primary.dark }}>
                            {displayedUser.mail}
                        </Typography>
                    </Box>
                )}

                {displayedUser?.phone && (
                    <Box sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}>
                        <Box sx={flexCenter}>
                            <FontAwesomeIcon
                                color={palette.grey[500]}
                                icon={faPhone}
                            />
                        </Box>
                        <LinkCallablePhone phone={displayedUser.phone} />
                    </Box>
                )}
                {displayedUser?.poste && (
                    <Box sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}>
                        <Box sx={flexCenter}>
                            <FontAwesomeIcon
                                color={palette.grey[500]}
                                icon={faPhone}
                            />
                        </Box>
                        <Typography sx={{ color: palette.primary.dark }}>
                            {displayedUser.poste}
                        </Typography>
                    </Box>
                )}
                {source === 'BACK' && displayedUser?.mobilePhone && (
                    <Box sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}>
                        <Box sx={flexCenter}>
                            <FontAwesomeIcon
                                color={palette.grey[500]}
                                icon={faPhone}
                            />
                        </Box>

                        <LinkCallablePhone phone={displayedUser.mobilePhone} />
                    </Box>
                )}

                {displayUserSchedule && (
                    <Box sx={{ ...flexCenter, alignItems: 'start', gap: '16px' }}>
                        <Box sx={flexCenter}>
                            <FontAwesomeIcon
                                color={palette.grey[500]}
                                icon={faClock}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                            <Typography
                                variant="l2dbodys"
                                color="text.gris50"
                            >
                                Du lundi au vendredi
                            </Typography>
                            <Typography
                                variant="l2dbodys"
                                color="text.gris50"
                            >
                                De 9h00 - 12h00, 14h00 - 17h00
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {displayActionContact && (
                <Box>
                    <Button
                        variant="contained"
                        onClick={handleButtonClick}
                        sx={{
                            backgroundColor: palette.primary.dark,
                            padding: '16px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0 10px',
                            width: '100%',
                            transition: 'all ease-in-out 300ms',
                            fontSize: '16px',
                            '&:hover': {
                                backgroundColor: palette.primary.main,
                            },
                        }}
                    >
                        Contacter
                        <FontAwesomeIcon
                            color="white"
                            icon={faChevronRight}
                        />
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default UserContactCard;
